import React, {useState} from 'react';
import {
  ThemeProvider,
  ValidationFlow,
  ValidationFlowProvider,
  Portal,
  ConfigProvider,
  Box,
  Heading,
  List,
  ListItem,
  Text
} from '@kaena1/mm-ui-components';
import {theme} from '@kaena1/mm-ui-theme-v3';
import {CheckCoverage} from '@kaena1/mm-ui-mint-components';
import {FocusOn} from 'react-focus-on';
import {dataLayer} from '@kaena1/mm-ui-utils';
import foxImage from '../../../../assets/images/fox-map-v3x2.png';
import styles from './checkCoverage.module.scss';
import '@kaena1/mm-ui-theme-v3/styles.css';
import {DarkGreenCheckmark} from './DarkGreenCheckmark';
import clsx from 'clsx';

type ZippopotamusResponse = {
  ok: boolean;
  status: number;
};

const errorMsg = 'Please input a valid zip code';
interface AppProps {
  vf?: VFData;
}
type VFData = {
  vfHeading?: string;
  vfSubheading?: string;
  vfBody?: string;
  vfLegal?: string;
  vfPage?: string;
  vfList?: string;
  vfCoverageText?: string;
};
export const App = ({vf}: AppProps) => {
  const [zipCode, setZipCode] = useState<string>();
  const [isVFOpen, setIsVFOpen] = useState<boolean>(false);
  const [errorState, setErrorState] = useState<boolean>(true);

  const handleError = (zipCode: string): void => {
    dataLayer.push({
      event: 'VF Zipcode Error',
      zip: zipCode,
      errorMessage: errorMsg,
      location: 'Coverage and Device Check',
    });
    setErrorState(false);
  };

  const handleOnSubmit = async (zipCode: string): Promise<void> => {
    if (zipCode.length !== 5 || zipCode === '00000')
      return handleError(zipCode);

    const res = (await fetch(
      'https://api.zippopotam.us/us/' + zipCode,
    )) as ZippopotamusResponse;

    if (!res.ok && res.status === 404) return handleError(zipCode);

    // valid zip code
    dataLayer.push({
      event: 'Validation Flow',
      'flow step': 'Entry',
      'entry location': 'Best Value Wireless Page',
    });
    setErrorState(true);
    setZipCode(zipCode);
    setIsVFOpen(true);
  };
  const {vfHeading, vfSubheading, vfBody, vfLegal, vfPage, vfList, vfCoverageText} = vf;
  const vfListParsed = vfList ? vfList.split(',') : undefined;
  const isBestValueWireless = vfPage === 'best-value-wireless';
  return (
    <>
      <ThemeProvider theme={theme}>
        <ConfigProvider
          config={{
            NEXT_PUBLIC_SITE_URL: process.env.SITE_URL,
          }}>
          <div className={clsx(styles.vfWrapper, {
                [styles.vfWrapperWithBody]: vfHeading && vfBody
              })}>
            <Box
              className={clsx({
                [styles.arm]: vfHeading,
                [styles.noArm]: vfHeading === undefined,
                [styles.armWithBody]: vfHeading && vfBody
              })}>
              {vfHeading && (
                <div className={styles.leftWrapper}>
                  <Heading
                    as="h2"
                    size="medium"
                    variant="headline"
                    className={styles.vfHeading}>
                    {vfHeading}
                  </Heading>
                  {vfSubheading && (
                    <Heading
                      as="h3"
                      variant="title"
                      className={styles.vfSubheading}>
                      {vfSubheading}
                    </Heading>
                  )}
                  {vfBody && (
                    <Text className={styles.vfBody}>
                      {vfBody}
                    </Text>
                  )}
                  {vfListParsed && (
                    <List
                      listTag="ul"
                      style={{
                        paddingLeft: isBestValueWireless && '1.25rem' 
                      }}
                      icon={!isBestValueWireless && <DarkGreenCheckmark />}>
                      {vfListParsed.map((item, index) => (
                        <ListItem
                          className={styles.listItem}
                          key={index}
                       > 
                          {item}
                        </ListItem>
                      ))}
                    </List>
                  )}
                </div>
              )}
              <div className={styles.checkCoverageContainer}>
                <CheckCoverage
                  onSubmit={handleOnSubmit}
                  image={
                    vfPage !== 'have-no-fear' && (
                      <img
                        src={foxImage}
                        alt="Fox looking at USA coverage map"
                      />
                    )
                  }
                  coverageText={vfCoverageText}
                  zipStatus={errorState}
                  errorMessage={errorMsg}
                />
              </div>
            </Box>
            {vfLegal && (
              <Text variant='legal' className={styles.vfLegal}>
                {vfLegal}
              </Text>
            )}
          </div>
          {isVFOpen && (
            <Portal>
              <FocusOn onEscapeKey={() => setIsVFOpen(false)}>
                <div className={styles.vfContainer}>
                  <div
                    style={{
                      overflow: 'auto',
                      height: '100dvh',
                    }}>
                    <ValidationFlowProvider
                      defaultAddresses={[
                        {
                          isInvalid: false,
                          streetAddress: '',
                          zipCode: zipCode,
                        },
                      ]}>
                      <ValidationFlow
                        onClose={() => {
                          setIsVFOpen(false);
                        }}
                      />
                    </ValidationFlowProvider>
                  </div>
                </div>
              </FocusOn>
            </Portal>
          )}
        </ConfigProvider>
      </ThemeProvider>
    </>
  );
};
